import React, { useState } from 'react';
import { Card, CardMedia, CardContent, Typography, Button, Chip, Box, IconButton, Fade, Zoom } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaChevronLeft, FaChevronRight, FaEye, FaShoppingCart } from 'react-icons/fa';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleViewDetails = () => {
    navigate(`/products/${product.id}`);
  };

  // Gérer les images : tableau ou image unique
  const images = product.images && product.images.length > 0 
    ? product.images 
    : (product.image ? [product.image] : ['https://via.placeholder.com/400x300?text=Produit']);

  const hasMultipleImages = images.length > 1;

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: '100%',
        height: '350px',
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: isHovered ? '#0077B8' : 'rgba(0, 0, 0, 0.08)',
        boxShadow: isHovered 
          ? '0 12px 28px rgba(0, 119, 184, 0.25)' 
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        cursor: 'pointer',
        background: '#ffffff',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #0077B8 0%, #00ACEA 50%, #0077B8 100%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.3s ease',
        },
      }} 
      onClick={handleViewDetails}
    >
      {/* Image Container */}
      <Box
        sx={{
          width: '100%',
          height: '200px',
          position: 'relative',
          bgcolor: '#f8fafc',
          overflow: 'hidden',
        }}
      >
        {/* Category Badge */}
        <Zoom in={true}>
          <Chip
            label={product.categorie_titre || 'Produit'}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 2,
              bgcolor: '#0077B8',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.688rem',
              height: 24,
              boxShadow: '0 2px 8px rgba(0, 119, 184, 0.3)',
            }}
          />
        </Zoom>

        {/* Image */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2.5,
          }}
        >
          <CardMedia
            component="img"
            image={images[currentImageIndex]}
            alt={product.titre}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
            }}
          />
        </Box>

        {/* Navigation flèches (si plusieurs images) */}
        {hasMultipleImages && (
          <Fade in={isHovered}>
            <Box>
              <IconButton
                onClick={handlePrevImage}
                size="small"
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    bgcolor: '#0077B8',
                    color: 'white',
                  },
                }}
              >
                <FaChevronLeft size={14} />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    bgcolor: '#0077B8',
                    color: 'white',
                  },
                }}
              >
                <FaChevronRight size={14} />
              </IconButton>
            </Box>
          </Fade>
        )}

        {/* Indicateur images */}
        {hasMultipleImages && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0, 119, 184, 0.9)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: '12px',
              fontSize: '0.688rem',
              fontWeight: 700,
            }}
          >
            {currentImageIndex + 1}/{images.length}
          </Box>
        )}

        {/* Overlay Quick View */}
        <Fade in={isHovered}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 119, 184, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 119, 184, 0.3)',
                color: '#0077B8',
              }}
            >
              <FaEye size={20} />
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Content */}
      <CardContent 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between',
          p: 2,
          pt: 1.5,
        }}
      >
        {/* Titre */}
        <Typography 
          variant="h6" 
          component="h3"
          sx={{
            fontSize: '0.938rem',
            fontWeight: 700,
            color: '#0c4a6e',
            mb: 1.5,
            lineHeight: 1.4,
            height: '42px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'color 0.2s ease',
            '&:hover': {
              color: '#0077B8',
            }
          }}
        >
          {product.titre}
        </Typography>

        {/* Footer */}
        <Box>
          {/* Disponibilité */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 1.5,
            }}
          >
            {product.disponibilite === 1 || product.disponibilite === '1' ? (
              <>
                <FaCheckCircle color="#10b981" size={14} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#10b981', 
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                >
                  Disponible
                </Typography>
              </>
            ) : (
              <>
                <FaTimesCircle color="#ef4444" size={14} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#ef4444', 
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                >
                  Indisponible
                </Typography>
              </>
            )}
          </Box>

          {/* Bouton Action */}
          <Button
            fullWidth
            variant={isHovered ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FaShoppingCart size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails();
            }}
            sx={{
              py: 1,
              fontWeight: 700,
              fontSize: '0.813rem',
              borderRadius: '8px',
              textTransform: 'none',
              borderColor: '#0077B8',
              color: isHovered ? 'white' : '#0077B8',
              bgcolor: isHovered ? '#0077B8' : 'transparent',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#00ACEA',
                borderColor: '#00ACEA',
                color: 'white',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 119, 184, 0.3)',
              }
            }}
          >
            Voir détails
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
